// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { dcbAtom } from '@shared/store/slice-atoms';
import { appendTextScript, getJsonParsed } from '@shared/utils/general';
import { isBH, isDZ, isIQ, isMA, isSA } from '@entities/country/model/utils';
import {
  isIraqAsiacell,
  isIraqZain,
  isKorek,
  isKSAMobily,
  isKSAVirgin,
  isKuwaitSTC,
  isKuwaitZainIdex,
  isTunisiaOrange,
} from '@entities/operator';
import { isIQFastLink, isKSARedbull } from '@entities/operator/model/utils';
import { useSLAOperators } from '@features/phone-subscription/model/hooks/useSLAOperators';
import { useIQFastLinkAntifraud } from '@features/phone-subscription/model/hooks/useIQFastlinkAntifraud';
import { IQ_ZAIN_ANTIFRAUD_ID_SS_NAME, clearIQZainAntifraudSession } from '@features/phone-subscription/model/hooks/useIQZainAntifraud';
import { OTPVariant } from '@features/otp-verification/model/types';

const STC_UNIQUE_ID_SS_NAME = 'STC_UNIQUE_ID';
const OTP_INPUT_ID = 'pin-input-full';

const focusOtpInput = () => document.getElementById(OTP_INPUT_ID)?.focus();

// Centralises antifraud script injection per operator. Standard and V5
// share most branches, but each has small differences (e.g. V5 doesn't
// dispatch DCBProtectRun for Virgin, standard injects on DZ/MA/SAMobily).
// The hook returns the resolved antifraud id and a setter the caller can
// reuse (e.g. PinForm's direct-redirect variant sets it from an API
// response). Pass `variant: "direct-redirect"` to skip injection entirely.
export const useOTPAntifraud = (variant: OTPVariant) => {
  const [antifraudUniqueId, setAntifraudUniqueId] = useState<string | null>(null);

  const dcb = useAtomValue(dcbAtom);
  const { homeData, subscribeData, operator, resendOtpData, fallbackOperator } = dcb as any;

  const { isBHZainSLAOperator, isMAUmobileOperator } = useSLAOperators();
  const { isIQFastlink } = useIQFastLinkAntifraud();
  const isSLAOperator = isBHZainSLAOperator || isMAUmobileOperator || isIQFastlink;

  const operatorIdentifier = subscribeData?.operatorName || operator?.value || fallbackOperator;
  const operatorName = subscribeData?.operatorName || operator?.name;

  const isVirgin = isSA(homeData?.country) && isKSAVirgin(operatorName);
  const isIQKorek = isIQ(homeData?.country) && isKorek(operatorIdentifier);
  const isIQAsiacell = isIQ(homeData?.country) && isIraqAsiacell(operatorIdentifier);
  const isSAMobily = isKSAMobily(operatorIdentifier);
  const isKWSTC = isKuwaitSTC(operatorIdentifier);
  const isKSARedBull = isKSARedbull(operatorIdentifier);
  const isKuwaitZain = isKuwaitZainIdex(operatorIdentifier);
  const isTNrange = isTunisiaOrange(operatorIdentifier);
  const isIraqFastLink = isIQFastLink(operatorIdentifier);
  const isIQZain = isIQ(homeData?.country) && isIraqZain(operatorIdentifier);

  const resendOtpJsstring = useMemo(() => {
    if (!resendOtpData?.response) return '';
    const parsed = getJsonParsed(resendOtpData.response);
    return parsed?.jsstring || parsed;
  }, [resendOtpData?.response]);

  // The standard and v5 runners share this hook, and a later state update
  // (Mobily reads its id from session storage) used to rebuild one runner
  // and re-run the other. appendTextScript has no id here, so that second
  // pass appended the same script again. Keep one copy of each payload.
  const injectedScriptsRef = useRef(new Set<string>());

  const appendScriptOnce = useCallback((script: string | undefined) => {
    if (!script || injectedScriptsRef.current.has(script)) return false;
    injectedScriptsRef.current.add(script);
    appendTextScript(script, 0);
    return true;
  }, []);

  const injectAndDispatchProtect = useCallback(
    (script: string | undefined) => {
      if (!script) return;
      const payload = resendOtpJsstring || script;
      if (!appendScriptOnce(payload)) return;
      const event = new Event('DCBProtectRun');
      document.dispatchEvent(event);
    },
    [appendScriptOnce, resendOtpJsstring]
  );

  const injectAndCaptureUniqueId = useCallback(
    (script: string | undefined) => {
      if (!script) return;
      const payload = resendOtpJsstring || script;
      appendScriptOnce(payload);
      setAntifraudUniqueId(subscribeData?.antifraudUniqueId);
    },
    [appendScriptOnce, resendOtpJsstring, subscribeData?.antifraudUniqueId]
  );

  const runStandardInjection = useCallback(() => {
    focusOtpInput();

    if (isIQKorek) injectAndDispatchProtect(subscribeData?.apiSubscribeServiceID);
    if (isVirgin) injectAndDispatchProtect(subscribeData?.apiSubscribeServiceID);
    if (isTNrange) injectAndDispatchProtect(subscribeData?.antifraudScript);

    if (isKWSTC) injectAndCaptureUniqueId(subscribeData?.antifraudScript);
    if (isKSARedBull) injectAndCaptureUniqueId(subscribeData?.antifraudScript);
    if (isKuwaitZain) injectAndCaptureUniqueId(subscribeData?.antifraudScript);

    if (isBH(homeData?.country) && !isBHZainSLAOperator) {
      injectAndCaptureUniqueId(subscribeData?.antifraudScript);
    }

    if (isMA(homeData?.country) && !isMAUmobileOperator) {
      setAntifraudUniqueId(subscribeData?.antifraudUniqueId || '');
    }

    if (isDZ(homeData?.country)) {
      injectAndDispatchProtect(subscribeData?.antifraudScript);
    }

    if (isSAMobily) {
      injectAndDispatchProtect(subscribeData?.antifraudScript);
    }
  }, [
    isIQKorek,
    isVirgin,
    isTNrange,
    isKWSTC,
    isKSARedBull,
    isKuwaitZain,
    isSAMobily,
    isBHZainSLAOperator,
    isMAUmobileOperator,
    homeData?.country,
    subscribeData?.apiSubscribeServiceID,
    subscribeData?.antifraudScript,
    subscribeData?.antifraudUniqueId,
    injectAndDispatchProtect,
    injectAndCaptureUniqueId,
  ]);

  const runV5Injection = useCallback(() => {
    focusOtpInput();

    if (isIQKorek) injectAndDispatchProtect(subscribeData?.apiSubscribeServiceID);

    if (isKSARedBull) injectAndCaptureUniqueId(subscribeData?.antifraudScript);
    if (isKWSTC) injectAndCaptureUniqueId(subscribeData?.antifraudScript);
    if (isIraqFastLink) injectAndCaptureUniqueId(subscribeData?.antifraudScript);
    if (isKuwaitZain) injectAndCaptureUniqueId(subscribeData?.antifraudScript);

    if (isVirgin && subscribeData?.apiSubscribeServiceID) {
      appendScriptOnce(resendOtpJsstring || subscribeData.apiSubscribeServiceID);
    }

    if (isIQAsiacell && subscribeData?.apiSubscribeServiceID && !antifraudUniqueId) {
      injectAndCaptureUniqueId(subscribeData?.apiSubscribeServiceID);
    }

    if (isBH(homeData?.country) && !isBHZainSLAOperator) {
      injectAndCaptureUniqueId(subscribeData?.antifraudScript);
    }
  }, [
    isIQKorek,
    isKSARedBull,
    isKWSTC,
    isIraqFastLink,
    isKuwaitZain,
    isVirgin,
    isIQAsiacell,
    isBHZainSLAOperator,
    antifraudUniqueId,
    resendOtpJsstring,
    appendScriptOnce,
    homeData?.country,
    subscribeData?.apiSubscribeServiceID,
    subscribeData?.antifraudScript,
    injectAndDispatchProtect,
    injectAndCaptureUniqueId,
  ]);

  // Kept as two effects so a v5-only dependency (for example the Mobily
  // session-storage id) cannot run the standard injector a second time.
  // direct-redirect skips operator injection because the antifraud id is
  // fetched from the redirection details API instead.
  useEffect(() => {
    if (variant !== 'standard') return;
    runStandardInjection();
  }, [variant, runStandardInjection]);

  useEffect(() => {
    if (variant !== 'v5') return;
    runV5Injection();
  }, [variant, runV5Injection]);

  // KSA Mobily pulls its id from session storage on standard variant.
  useEffect(() => {
    if (variant !== 'standard' || !isSAMobily) return;
    setAntifraudUniqueId(sessionStorage.getItem(STC_UNIQUE_ID_SS_NAME) || '');
  }, [variant, isSAMobily]);

  useEffect(() => {
    if (variant === 'direct-redirect' || !isIQZain) return;
    setAntifraudUniqueId(sessionStorage.getItem(IQ_ZAIN_ANTIFRAUD_ID_SS_NAME) || '');

    return () => {
      clearIQZainAntifraudSession();
    };
  }, [variant, isIQZain]);

  // Standard splits IQ Asiacell into its own effect on the live form.
  useEffect(() => {
    if (variant !== 'standard') return;
    if (isIQAsiacell && subscribeData?.apiSubscribeServiceID) {
      appendScriptOnce(subscribeData.apiSubscribeServiceID);
      setAntifraudUniqueId(subscribeData?.antifraudUniqueId);
    }
  }, [
    variant,
    isIQAsiacell,
    appendScriptOnce,
    subscribeData?.apiSubscribeServiceID,
    subscribeData?.antifraudUniqueId,
  ]);

  return {
    antifraudUniqueId,
    setAntifraudUniqueId,
    isBHZainSLAOperator,
    isMAUmobileOperator,
    isIQFastlink,
    isSLAOperator,
    resendOtpJsstring,
  };
};
